import React from 'react';
import { LayoutDashboard, GraduationCap, UserCircle, MessageSquare, BookOpen, Clock, Settings } from 'lucide-react';

export interface HelpItem {
    term: string;
    definition: string;
    example?: string;
    tip?: string;
}

export interface PageHelpContent {
    id: string;
    title: string;
    icon: React.ReactNode;
    color: 'indigo' | 'blue' | 'emerald' | 'purple' | 'amber' | 'rose';
    intro: string;
    sections: {
        title: string;
        items: HelpItem[];
    }[];
}

export const pageHelpData: Record<string, PageHelpContent> = {
    dashboard: {
        id: 'dashboard',
        title: 'Resumen de finanzas',
        icon: <LayoutDashboard className="w-6 h-6" />,
        color: 'indigo',
        intro: 'Consulta lo que has ingresado, lo que has gastado y el resultado del mes.',
        sections: [
            {
                title: 'Cifras principales',
                items: [
                    {
                        term: 'Ingresos del mes',
                        definition: 'Suma de las ventas registradas durante el mes.',
                        tip: 'Compáralos con un mes anterior que también esté cerrado.'
                    },
                    {
                        term: 'Beneficio estimado',
                        definition: 'Lo que queda después de restar gastos e impuestos estimados.',
                        tip: 'La cifra es provisional hasta que cierres el mes.'
                    }
                ]
            }
        ]
    },
    operations: {
        id: 'operations',
        title: 'Turnos y equipo',
        icon: <Clock className="w-6 h-6" />,
        color: 'blue',
        intro: 'Organiza horarios, repartidores y vehículos desde un único lugar.',
        sections: [
            {
                title: 'Planificación semanal',
                items: [
                    {
                        term: 'Calendario de turnos',
                        definition: 'Cada fila corresponde a un repartidor y muestra sus turnos de la semana.',
                        tip: 'Pulsa un turno para editarlo, copiarlo o borrarlo.'
                    },
                    {
                        term: 'Mover un turno',
                        definition: 'Arrastra el turno hasta el día y la hora que necesites.',
                        tip: 'Verás una vista previa antes de soltarlo.'
                    }
                ]
            },
            {
                title: 'Vehículos',
                items: [
                    {
                        term: 'Lista de vehículos',
                        definition: 'Muestra el estado, la batería y los mantenimientos de cada vehículo.',
                        tip: 'Filtra por vehículos disponibles o en el taller.'
                    }
                ]
            }
        ]
    },
    academy: {
        id: 'academy',
        title: 'Academia',
        icon: <GraduationCap className="w-6 h-6" />,
        color: 'emerald',
        intro: 'Forma a tu equipo con módulos interactivos y seguimiento de progreso.',
        sections: [
            {
                title: 'Cómo aprender',
                items: [
                    {
                        term: 'Vídeo o texto',
                        definition: 'Elige cómo quieres seguir cada lección cuando ambas opciones estén disponibles.',
                        tip: 'Tu avance se guarda automáticamente.'
                    }
                ]
            }
        ]
    },
    profile: {
        id: 'profile',
        title: 'Perfil y ajustes',
        icon: <UserCircle className="w-6 h-6" />,
        color: 'purple',
        intro: 'Revisa tus datos, tu acceso y las preferencias de la aplicación.',
        sections: [
            {
                title: 'Tu cuenta',
                items: [
                    {
                        term: 'Menú de usuario',
                        definition: 'Pulsa tu avatar para abrir tu perfil o cerrar la sesión.',
                        tip: 'En móvil, los ajustes están en la barra inferior.'
                    },
                    {
                        term: 'Modo Oscuro',
                        definition: 'Alterna entre tema claro y oscuro desde el botón junto a tu versión de app.',
                        tip: 'Elige el tema que te resulte más cómodo.'
                    }
                ]
            }
        ]
    },
    sidebar_config: {
        id: 'sidebar_config',
        title: 'Ajustes de finanzas',
        icon: <Settings className="w-6 h-6" />,
        color: 'amber',
        intro: 'Indica las tarifas y gastos que se usarán en los cálculos.',
        sections: [
            {
                title: 'Tarifas y gastos',
                items: [
                    {
                        term: 'Precios por entrega',
                        definition: 'Define cuánto cobras según el pedido y la distancia.',
                        tip: 'Los cambios actualizan los cálculos del mes actual.'
                    }
                ]
            }
        ]
    },
    support: {
        id: 'support',
        title: 'Ayuda y Soporte',
        icon: <MessageSquare className="w-6 h-6" />,
        color: 'rose',
        intro: 'Centro de resolución de dudas y contacto con central.',
        sections: [
            {
                title: 'Consultas',
                items: [
                    {
                        term: 'Tipo de consulta',
                        definition: 'Indica si el problema es general, técnico o de facturas para que llegue al equipo correcto.',
                        tip: 'Consulta primero la ayuda disponible; puede que la respuesta ya esté publicada.'
                    }
                ]
            }
        ]
    },
    resources: {
        id: 'resources',
        title: 'Documentos',
        icon: <BookOpen className="w-6 h-6" />,
        color: 'blue',
        intro: 'Documentación oficial, contratos y manuales operativos.',
        sections: [
            {
                title: 'Archivos y guías',
                items: [
                    {
                        term: 'Guías paso a paso',
                        definition: 'Explican qué hacer ante situaciones habituales, como un accidente o una inspección.',
                        tip: 'Descarga los PDF importantes para consultarlos sin conexión.'
                    }
                ]
            }
        ]
    }
};
